import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import ImageUploader from "../components/ImageUploader";

const inputCls = "bg-[#121212] border-axe-border rounded-xl text-white h-12 px-4";
const taCls = "bg-[#121212] border-axe-border rounded-xl text-white p-4";

export default function SiteContentEditor() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/site-content").then((r) => setC(r.data)).catch(() => {});
  }, []);

  if (!c) return <div className="text-neutral-400">Loading…</div>;

  function update(k, v) {
    setC({ ...c, [k]: v });
  }
  function updateList(k, fn) {
    setC({ ...c, [k]: fn([...(c[k] || [])]) });
  }

  async function save() {
    setSaving(true);
    try {
      await api.put("/site-content", {
        about_eyebrow: c.about_eyebrow,
        about_headline: c.about_headline,
        about_mission: c.about_mission,
        about_why: c.about_why,
        about_image: c.about_image,
        about_stats: c.about_stats || [],
        about_values: c.about_values || [],
        contact_blurb: c.contact_blurb,
        contact_emails: c.contact_emails || [],
        terms_content: c.terms_content,
        privacy_content: c.privacy_content,
      });
      toast.success("Site content saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-8" data-testid="site-content-editor">
      <section className="rounded-2xl border border-axe-border bg-axe-surface p-6 md:p-8">
        <h3 className="font-display font-bold text-xl mb-5">About Page</h3>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Eyebrow</Label>
            <Input value={c.about_eyebrow || ""} onChange={(e) => update("about_eyebrow", e.target.value)} className={`mt-2 ${inputCls}`} data-testid="sc-eyebrow" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Headline</Label>
            <Input value={c.about_headline || ""} onChange={(e) => update("about_headline", e.target.value)} className={`mt-2 ${inputCls}`} data-testid="sc-headline" />
          </div>
        </div>
        <div className="mt-5">
          <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Mission</Label>
          <Textarea value={c.about_mission || ""} onChange={(e) => update("about_mission", e.target.value)} className={`mt-2 ${taCls} min-h-[100px]`} data-testid="sc-mission" />
        </div>
        <div className="mt-5">
          <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Why AXE</Label>
          <Textarea value={c.about_why || ""} onChange={(e) => update("about_why", e.target.value)} className={`mt-2 ${taCls} min-h-[100px]`} data-testid="sc-why" />
        </div>
        <div className="mt-5">
          <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">About Image (team victory)</Label>
          <div className="mt-2 max-w-md">
            <ImageUploader value={c.about_image} onChange={(url) => update("about_image", url)} aspect="aspect-[4/5]" testId="sc-about-image" />
          </div>
        </div>

        <div className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Stats Strip</Label>
            <button type="button" onClick={() => updateList("about_stats", (arr) => [...arr, { key: "", value: "" }])} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] font-bold text-axe-tangerine" data-testid="sc-stats-add"><Plus size={12} /> Add</button>
          </div>
          <div className="space-y-2">
            {(c.about_stats || []).map((s, i) => (
              <div key={i} className="flex gap-2" data-testid={`sc-stat-${i}`}>
                <Input value={s.key} onChange={(e) => updateList("about_stats", (arr) => arr.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)))} className={inputCls} placeholder="Label" />
                <Input value={s.value} onChange={(e) => updateList("about_stats", (arr) => arr.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} className={inputCls} placeholder="Value" />
                <button type="button" onClick={() => updateList("about_stats", (arr) => arr.filter((_, j) => j !== i))} className="px-3 text-red-400 hover:bg-axe-bg rounded-xl" aria-label="Remove"><X size={16} /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Core Values</Label>
            <button type="button" onClick={() => updateList("about_values", (arr) => [...arr, { title: "", text: "" }])} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] font-bold text-axe-tangerine" data-testid="sc-values-add"><Plus size={12} /> Add</button>
          </div>
          <div className="space-y-3">
            {(c.about_values || []).map((v, i) => (
              <div key={i} className="rounded-xl border border-axe-border bg-axe-bg p-3 space-y-2" data-testid={`sc-value-${i}`}>
                <div className="flex gap-2 items-center">
                  <Input value={v.title} onChange={(e) => updateList("about_values", (arr) => arr.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} className={inputCls} placeholder="Title" />
                  <button type="button" onClick={() => updateList("about_values", (arr) => arr.filter((_, j) => j !== i))} className="px-3 text-red-400 hover:bg-axe-surface rounded-xl" aria-label="Remove"><X size={16} /></button>
                </div>
                <Textarea value={v.text} onChange={(e) => updateList("about_values", (arr) => arr.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} className={`${taCls} min-h-[60px]`} placeholder="Description" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-axe-border bg-axe-surface p-6 md:p-8">
        <h3 className="font-display font-bold text-xl mb-5">Contact Page</h3>
        <div>
          <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Blurb</Label>
          <Textarea value={c.contact_blurb || ""} onChange={(e) => update("contact_blurb", e.target.value)} className={`mt-2 ${taCls} min-h-[80px]`} data-testid="sc-contact-blurb" />
        </div>
        <div className="mt-5">
          <div className="flex items-center justify-between mb-3">
            <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Contact Emails</Label>
            <button type="button" onClick={() => updateList("contact_emails", (arr) => [...arr, { label: "", email: "" }])} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] font-bold text-axe-tangerine" data-testid="sc-emails-add"><Plus size={12} /> Add</button>
          </div>
          <div className="space-y-2">
            {(c.contact_emails || []).map((e, i) => (
              <div key={i} className="flex gap-2" data-testid={`sc-email-${i}`}>
                <Input value={e.label} onChange={(ev) => updateList("contact_emails", (arr) => arr.map((x, j) => (j === i ? { ...x, label: ev.target.value } : x)))} className={inputCls} placeholder="Partnerships" />
                <Input value={e.email} onChange={(ev) => updateList("contact_emails", (arr) => arr.map((x, j) => (j === i ? { ...x, email: ev.target.value } : x)))} className={inputCls} placeholder="partnerships@axe.gg" />
                <button type="button" onClick={() => updateList("contact_emails", (arr) => arr.filter((_, j) => j !== i))} className="px-3 text-red-400 hover:bg-axe-bg rounded-xl" aria-label="Remove"><X size={16} /></button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-axe-border bg-axe-surface p-6 md:p-8">
        <h3 className="font-display font-bold text-xl mb-5">Legal</h3>
        <div>
          <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Terms of Service (Markdown: # heading, ## subheading, - list)</Label>
          <Textarea value={c.terms_content || ""} onChange={(e) => update("terms_content", e.target.value)} className={`mt-2 ${taCls} min-h-[220px] font-mono text-sm`} data-testid="sc-terms" />
        </div>
        <div className="mt-5">
          <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Privacy Policy</Label>
          <Textarea value={c.privacy_content || ""} onChange={(e) => update("privacy_content", e.target.value)} className={`mt-2 ${taCls} min-h-[220px] font-mono text-sm`} data-testid="sc-privacy" />
        </div>
      </section>

      <div className="sticky bottom-4 flex justify-end">
        <button onClick={save} disabled={saving} className="rounded-full bg-axe-purple px-8 py-3.5 text-xs uppercase tracking-[0.22em] font-bold text-white hover:bg-axe-purple-hover disabled:opacity-50 drop-shadow-[0_0_14px_rgba(176,38,255,0.45)]" data-testid="sc-save">
          {saving ? "Saving…" : "Save All Content"}
        </button>
      </div>
    </div>
  );
}
